/* Country store picker (replaces Cozy Country Redirect): local-store pop-up and country blocking.
 * Config comes from snippets/gp-store-picker.liquid. */
(() => {
  'use strict';
  const configEl = document.getElementById('gp-store-picker-config');
  if (!configEl || window.GrowplayStorePicker) return;
  window.GrowplayStorePicker = true;
  const cfg = JSON.parse(configEl.textContent);
  const root = document.documentElement;
  const CHOICE_KEY = 'gp-store-picker-choice';
  const STAFF_KEY = 'gp-store-picker-staff';
  const COUNTRY_KEY = 'gp-store-picker-country';
  const BLOCKED_KEY = 'gp-store-picker-blocked';
  // Search engines and link previews must always see the page as it is. (Pinterest and Telegram
  // crawlers match "bot"; their in-app browsers are real shoppers.)
  const BOT = /bot|crawl|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|whatsapp|lighthouse|pagespeed|gtmetrix/i;
  const params = new URLSearchParams(location.search);

  const saved = {
    get(key) {
      try {
        const item = JSON.parse(localStorage.getItem(key));
        return item && item.expires > Date.now() ? item.value : null;
      } catch (e) { return null; }
    },
    set(key, value, days) {
      try { localStorage.setItem(key, JSON.stringify({ value, expires: Date.now() + days * 864e5 })); } catch (e) {}
    },
    remove(key) {
      try { localStorage.removeItem(key); } catch (e) {}
    },
  };
  const session = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (e) {} },
    remove(key) { try { sessionStorage.removeItem(key); } catch (e) {} },
  };
  const reveal = () => {
    const hold = document.getElementById('gp-store-picker-hold');
    if (hold) hold.remove();
  };

  async function sha256(text) {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Staff skip blocking: customers tagged "staff", or a device that opened a page with ?staff=CODE.
  async function isStaff() {
    if (cfg.staff || saved.get(STAFF_KEY)) return true;
    const code = params.get('staff');
    if (!code || !cfg.staffHash || !window.crypto || !crypto.subtle) return false;
    if ((await sha256(code.trim())) !== cfg.staffHash) return false;
    saved.set(STAFF_KEY, true, 365);
    params.delete('staff');
    history.replaceState(null, '', location.pathname + (params.toString() ? `?${params}` : '') + location.hash);
    return true;
  }

  // ?gp_country=GB tries the picker as a visitor from that country (preview themes and staff only).
  function testCountry(staff) {
    const isPreviewTheme = window.Shopify && Shopify.theme && Shopify.theme.role !== 'main';
    const value = (params.get('gp_country') || '').toUpperCase();
    if (value && (isPreviewTheme || staff)) {
      if (value === 'CLEAR') session.remove(COUNTRY_KEY + '-test');
      else session.set(COUNTRY_KEY + '-test', value);
    }
    return (isPreviewTheme || staff) ? session.get(COUNTRY_KEY + '-test') : null;
  }

  async function visitorCountry(staff) {
    const test = testCountry(staff);
    if (test) return test;
    const cached = session.get(COUNTRY_KEY);
    if (cached) return cached;
    const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const country = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
    if (country) session.set(COUNTRY_KEY, country);
    return country || null;
  }

  function countryName(code) {
    try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code); } catch (e) { return code; }
  }

  // The country as it reads in a sentence: "the United Kingdom", "Australia".
  function placeName(code) {
    return `${['GB', 'US', 'UM'].includes(code) ? 'the ' : ''}${countryName(code)}`;
  }

  function flag(store, width, height) {
    const img = document.createElement('img');
    img.src = store.flag;
    img.alt = '';
    img.width = width;
    img.height = height;
    return img;
  }

  function loadStyles() {
    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cfg.css;
      link.onload = link.onerror = resolve;
      document.head.append(link);
    });
  }

  // The same page on another store, from the hreflang links; that store's home page otherwise.
  function storeLink(store) {
    const alternate = [...document.querySelectorAll('link[rel="alternate"][hreflang]')]
      .find((link) => link.hreflang.toLowerCase() === store.hreflang.toLowerCase());
    return alternate ? alternate.href : `${store.url}/`;
  }

  let blocking = false;
  function block(country) {
    blocking = true;
    session.set(BLOCKED_KEY, '1');
    if (cfg.blockAction !== 'message') {
      location.replace('https://www.google.com/');
      return;
    }
    loadStyles().then(() => {
      const others = cfg.stores.filter((s) => s.code !== cfg.store);
      const page = document.createElement('div');
      page.className = 'gp-store-blocked';
      page.setAttribute('role', 'alert');
      page.innerHTML = `
        <div class="gp-store-blocked__inner">
          <h1 class="gp-store-blocked__title"></h1>
          <p class="gp-store-picker__intro">Growplay delivers to Australia, New Zealand, Great Britain and the United States.</p>
          <ul class="gp-store-blocked__stores"></ul>
        </div>`;
      page.querySelector('h1').textContent = `Sorry, this store doesn't deliver to ${countryName(country)}.`;
      const list = page.querySelector('ul');
      others.forEach((store) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `${store.url}/`;
        link.textContent = `Growplay ${store.name}`;
        item.append(link);
        list.append(item);
      });
      root.classList.add('gp-store-picker-locked');
      document.body.append(page);
      reveal();
    });
  }

  // Suggests the store for the visitor's country as the one main button; staying is the second
  // choice, and the other stores are small links for anyone shopping for somewhere else.
  function showPicker(country) {
    const here = cfg.stores.find((s) => s.code === cfg.store);
    const local = cfg.stores.find((s) => s.code === cfg.countryStore[country]);
    if (!here || !local) return;
    loadStyles().then(() => {
      const opener = document.activeElement;
      const overlay = document.createElement('div');
      overlay.className = 'gp-store-picker';
      overlay.innerHTML = `
        <div class="gp-store-picker__dialog" role="dialog" aria-modal="true" aria-labelledby="gp-store-picker-title" aria-describedby="gp-store-picker-intro" tabindex="-1">
          <button type="button" class="gp-store-picker__close" aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>
          </button>
          <h2 class="gp-store-picker__title" id="gp-store-picker-title"></h2>
          <p class="gp-store-picker__intro" id="gp-store-picker-intro"></p>
          <a class="gp-store-picker__go"><span></span></a>
          <button type="button" class="gp-store-picker__stay"></button>
          <p class="gp-store-picker__others"><span>Other stores</span></p>
        </div>`;
      overlay.querySelector('.gp-store-picker__title').textContent = `Shopping from ${placeName(country)}?`;
      // Why switch: the store they're on can't deliver to them. Visitors the suggested store can't ship to
      // either (Ireland -> UK store, US Outlying Islands -> US store) are told where it ships instead.
      overlay.querySelector('.gp-store-picker__intro').textContent = local.country === country
        ? `You're on our ${here.short} store, which only ships within ${here.delivers}.`
        : `Our ${local.short} store shows prices in ${local.currency}, but only ships within ${local.delivers}.`;
      const go = overlay.querySelector('.gp-store-picker__go');
      go.href = storeLink(local);
      go.dataset.store = local.code;
      go.prepend(flag(local, 24, 17));
      go.querySelector('span').textContent = `Go to the ${local.short} store`;
      overlay.querySelector('.gp-store-picker__stay').textContent = `Stay on the ${here.short} store`;
      const others = overlay.querySelector('.gp-store-picker__others');
      const rest = cfg.stores.filter((s) => s !== here && s !== local);
      rest.forEach((store) => {
        const link = document.createElement('a');
        link.href = storeLink(store);
        link.dataset.store = store.code;
        link.append(flag(store, 20, 14), store.name);
        others.append(link);
      });
      if (!rest.length) others.remove();

      const close = () => {
        // Staying (or closing) hides the pop-up for 30 days, as Cozy did.
        saved.set(CHOICE_KEY, cfg.store, 30);
        overlay.remove();
        root.classList.remove('gp-store-picker-open');
        document.removeEventListener('keydown', onKey);
        if (opener && opener.focus) opener.focus();
      };
      const onKey = (event) => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab') return;
        const focusable = [...overlay.querySelectorAll('a, button')];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        // Keep focus in the pop-up, also when it starts on the dialog itself.
        const inside = focusable.includes(document.activeElement);
        if (event.shiftKey && (!inside || document.activeElement === first)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (!inside || document.activeElement === last)) { event.preventDefault(); first.focus(); }
      };
      overlay.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-store]');
        // Switching stores hides the pop-up here for 7 days, as Cozy did.
        if (link) { saved.set(CHOICE_KEY, link.dataset.store, 7); return; }
        if (event.target === overlay || event.target.closest('.gp-store-picker__close, .gp-store-picker__stay')) close();
      });
      document.addEventListener('keydown', onKey);
      root.classList.add('gp-store-picker-open');
      document.body.append(overlay);
      // Screen readers announce the title and the suggestion; Tab then moves through the choices.
      overlay.querySelector('.gp-store-picker__dialog').focus();
    });
  }

  async function run() {
    if (params.get('gp_store_picker') === 'reset') { saved.remove(CHOICE_KEY); session.remove(BLOCKED_KEY); }
    if (BOT.test(navigator.userAgent)) return;
    const staff = await isStaff();
    const country = await visitorCountry(staff);
    if (!country) return;
    if (cfg.block && cfg.blockList.includes(country) && !staff && !cfg.preview) { block(country); return; }
    session.remove(BLOCKED_KEY);
    const home = cfg.countryStore[country];
    if (cfg.popup && home && home !== cfg.store && !saved.get(CHOICE_KEY)) showPicker(country);
  }

  // A blocked visitor stays hidden until they leave or the message is up; everyone else sees the page.
  run().catch(() => {}).finally(() => {
    if (!blocking) reveal();
  });
})();
